import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../hooks/useAuth';

// Admin emails — add yours here
// In production, move this to a Supabase role or env variable
const ADMIN_EMAILS = [
  'mjonthemove@gmail.com',
];

export default function Moderation() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [pending,  setPending]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [counts,   setCounts]   = useState({ pending: 0, approved: 0, total: 0 });

  // Guard — non-admins get redirected
  useEffect(() => {
    if (user && !ADMIN_EMAILS.includes(user.email)) {
      navigate('/feed');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchPending();
    fetchCounts();
  }, []);

  async function fetchPending() {
    setLoading(true);
    const { data } = await supabase
      .from('profiles')
      .select('id, display_name, profile_photo_url, created_at, date_of_birth')
      .eq('photo_approved', false)
      .not('profile_photo_url', 'is', null)
      .order('created_at', { ascending: true });
    setPending(data || []);
    setLoading(false);
  }

  async function fetchCounts() {
    const { count: total }    = await supabase.from('profiles').select('*', { count: 'exact', head: true });
    const { count: approved } = await supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('photo_approved', true);
    const { count: pending }  = await supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('photo_approved', false).not('profile_photo_url', 'is', null);
    setCounts({ total: total || 0, approved: approved || 0, pending: pending || 0 });
  }

  async function approve(id) {
    await supabase.from('profiles').update({ photo_approved: true }).eq('id', id);
    setPending(p => p.filter(u => u.id !== id));
    setCounts(c => ({ ...c, pending: c.pending - 1, approved: c.approved + 1 }));
  }

  async function reject(id) {
    await supabase.from('profiles')
      .update({ profile_photo_url: null, photo_approved: false })
      .eq('id', id);
    setPending(p => p.filter(u => u.id !== id));
    setCounts(c => ({ ...c, pending: c.pending - 1 }));
  }

  function getAge(dob) {
    if (!dob) return 'Unknown';
    const age = Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000));
    return `${age} years old`;
  }

  function formatDate(ts) {
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  if (!user || !ADMIN_EMAILS.includes(user.email)) return null;

  return (
    <div className="page" style={{ paddingTop: 80 }}>
      <div className="container-wide">

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', color: 'var(--gold)', fontSize: '2.2rem' }}>
              Moderation queue
            </h2>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: 4 }}>
              Review and approve incoming profile photos
            </p>
          </div>
          <button className="btn-ghost" onClick={fetchPending} style={{ fontSize: '0.85rem' }}>
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
          {[
            { label: 'Pending review', value: counts.pending, color: 'var(--gold)' },
            { label: 'Approved',       value: counts.approved, color: 'var(--valid-color)' },
            { label: 'Total profiles', value: counts.total,   color: 'var(--text)' },
          ].map(({ label, value, color }) => (
            <div key={label} className="card" style={{ padding: '20px 24px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: '2.4rem', color }}>{value}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Queue */}
        {loading ? (
          <p className="text-muted text-center" style={{ fontFamily: 'var(--font-heading)', fontSize: '1.4rem' }}>
            Loading queue...
          </p>
        ) : pending.length === 0 ? (
          <div className="card text-center" style={{ padding: 48 }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', color: 'var(--valid-color)', fontSize: '1.8rem' }}>
              Queue is clear.
            </h3>
            <p className="text-muted mt-16" style={{ fontSize: '0.9rem' }}>
              No photos pending review. Check back when new users sign up.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {pending.map(profile => (
              <div key={profile.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Photo */}
                <div style={{ position: 'relative' }}>
                  <img
                    src={profile.profile_photo_url}
                    alt={profile.display_name}
                    style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block' }}
                  />
                  {/* Gradient overlay */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%',
                    background: 'linear-gradient(to top, rgba(13,13,13,0.95), transparent)',
                    pointerEvents: 'none',
                  }} />
                  <div style={{ position: 'absolute', bottom: 12, left: 16, right: 16 }}>
                    <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.4rem', color: 'white' }}>
                      {profile.display_name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
                      {getAge(profile.date_of_birth)} · Submitted {formatDate(profile.created_at)}
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                  <button
                    onClick={() => reject(profile.id)}
                    style={{
                      padding: '14px',
                      background: 'rgba(248,113,113,0.1)',
                      border: 'none',
                      borderTop: '1px solid var(--border)',
                      borderRight: '1px solid var(--border)',
                      color: 'var(--error)',
                      fontFamily: 'var(--font-heading)',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      letterSpacing: '0.05em',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(248,113,113,0.2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(248,113,113,0.1)'}
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => approve(profile.id)}
                    style={{
                      padding: '14px',
                      background: 'rgba(74,222,128,0.1)',
                      border: 'none',
                      borderTop: '1px solid var(--border)',
                      color: 'var(--valid-color)',
                      fontFamily: 'var(--font-heading)',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      letterSpacing: '0.05em',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(74,222,128,0.2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(74,222,128,0.1)'}
                  >
                    Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
